import { Feature } from './feature';
import { getRecurlyAddOnName } from './addOn';

/**
 * One entry of the LaunchDarkly `trueBundleConfig` flag — the SAME flag the BE reads. It is the
 * single source of truth for what a True Bundle contains and for how long it is sold:
 *
 * - `addOns` maps a Recurly add-on CODE to the quantity included in the package. The FE only uses
 *   it to list the components and to send `addOns` with the preview/checkout request; the BE
 *   re-validates the set and answers `bundle.addons.mismatch` when it disagrees.
 * - `sellUntil` is an ISO-8601 instant WITH offset (e.g. `2026-10-31T23:59:59-07:00`). Past that
 *   moment the card disappears and the BE answers `bundle.sale.ended`; `null`/absent = no end.
 *
 * There is no boolean feature flag: an empty config (or a missing entry) hides the bundle
 * (Decision 15).
 */
export interface TrueBundleConfig {
  feature: string;
  addOns: Record<string, number>;
  sellUntil?: string | null;
}

export const getTrueBundleConfig = (
  configs: TrueBundleConfig[] | undefined,
  feature: Feature,
): TrueBundleConfig | undefined => (configs ?? []).find(
  config => config.feature === feature && Object.keys(config.addOns ?? {}).length > 0,
);

/**
 * Whether the bundle is still on sale. Compared on the epoch millisecond, never on a local
 * calendar date: `sellUntil` carries its own offset and the browser's timezone is irrelevant.
 * An unparseable value is treated as "no end date" rather than hiding a bundle that is on sale.
 */
export const isTrueBundleOnSale = (
  config: TrueBundleConfig | undefined,
  now: number = Date.now(),
): boolean => {
  if (!config) {
    return false;
  }
  if (!config.sellUntil) {
    return true;
  }
  const sellUntil = Date.parse(config.sellUntil);
  return Number.isNaN(sellUntil) || now < sellUntil;
};

/**
 * The last day of the offer as the CAMPAIGN states it ("until Oct 31, 2026"), or undefined when
 * the bundle has no end date.
 *
 * Deliberately the calendar date carried by `sellUntil`'s own offset, not the instant rendered in
 * the viewer's timezone: `2026-10-31T23:59:59-07:00` is already Nov 1 in UTC, so formatting the
 * instant would advertise a date the marketing copy, the campaign and the BE guard all disagree
 * with. The on-sale decision still compares the exact instant (see [isTrueBundleOnSale]).
 */
export const getTrueBundleSellUntilDate = (
  config: TrueBundleConfig | undefined,
): Date | undefined => {
  const sellUntil = config?.sellUntil;
  if (!sellUntil || Number.isNaN(Date.parse(sellUntil))) {
    return undefined;
  }
  const isoDate = /^(\d{4})-(\d{2})-(\d{2})/.exec(sellUntil);
  return isoDate
    // Midday keeps the date stable against any DST shift of the viewer's timezone.
    ? new Date(Number(isoDate[1]), Number(isoDate[2]) - 1, Number(isoDate[3]), 12)
    : new Date(Date.parse(sellUntil));
};

/**
 * `addOns` payload for a preview/checkout request: keyed by the BE `RecurlyAddon` enum NAME, not
 * by the add-on code (see `getRecurlyAddOnName`). Zero/negative quantities are dropped.
 */
export const getTrueBundleAddOnsPayload = (
  config: TrueBundleConfig | undefined,
): Record<string, number> => Object.fromEntries(
  Object.entries(config?.addOns ?? {})
    .filter(([, quantity]) => quantity > 0)
    .map(([addOnCode, quantity]) => [getRecurlyAddOnName(addOnCode), quantity]),
);

/** Components of the bundle as [addOnCode, quantity] pairs, in config order. */
export const getTrueBundleComponents = (
  config: TrueBundleConfig | undefined,
): Array<[string, number]> => Object.entries(config?.addOns ?? {})
  .filter(([, quantity]) => quantity > 0);
